import { useCallback, useContext, useMemo } from 'react'

import {
    getCoinListOnCoinSelection,
    validateAffectedSelectedCoin,
    requestGetPairRates,
} from 'nexchange-service/module/coins'
import { ShowWarningAction, WarningContext } from 'nexchange-service/module/common'

import { ECoinInputType } from '../constants'
import {
    AvailableCurrenciesContext,
    SelectCoinsReducerAction,
    SelectedCoinsContext,
} from '../stateManagement'
import { useOnGetPairErrorHandler, useOnGetPairSuccessHandler } from './handleResponse'
import { useCoinInputLoadingSetter } from './useCoinInputLoadingSetter'

type TUseCoinSelectionHandlerProps = {
    coinTypeFrom: ECoinInputType
    coinTypeTo: ECoinInputType
    buyMode?: boolean
}

const useCoinSelectionHandler = ({ coinTypeFrom, coinTypeTo, buyMode=true}: TUseCoinSelectionHandlerProps) => {
    const { dispatchAction: dispatchWarningAction } = useContext(WarningContext)

    const {
        state: {
            availablePairs: { fromPairs, toPairs },
            currencies,
        },
    } = useContext(AvailableCurrenciesContext)

    const {
        state: { from: coinFrom, to: coinTo },
        dispatchAction: dispatchSelectedCoins,
    } = useContext(SelectedCoinsContext)

    const setCoinInputsLoading = useCoinInputLoadingSetter()
    const onGetPairSuccess = useOnGetPairSuccessHandler()
    const onGetPairError = useOnGetPairErrorHandler()

    const getPairData = useCallback(
        async ({ changedInputType, changedCoinDto, affectedCoinDto }) => {
            const [coinCodeFrom, coinCodeTo, affectedInputType] =
                changedInputType === ECoinInputType.FROM
                    ? [changedCoinDto.code, affectedCoinDto.code, ECoinInputType.TO]
                    : [affectedCoinDto.code, changedCoinDto.code, ECoinInputType.FROM]

            return requestGetPairRates({
                amount: affectedCoinDto.code,
                amountOf: buyMode ? affectedInputType : changedInputType,
                fromCode: coinCodeFrom,
                toCode: coinCodeTo,
            })
        },
        [],
    )

    const setAvailableCoinList = useCallback(
        ({ changedInputType, affectedCoinList }) =>
            dispatchSelectedCoins({
                type: SelectCoinsReducerAction.CHANGE_COIN_LISTS,
                // @ts-ignore
                payload:
                    changedInputType === ECoinInputType.FROM
                        ? { coinListTo: affectedCoinList }
                        : { coinListFrom: affectedCoinList },
            }),
        [dispatchSelectedCoins],
    )

    const getCoinSelectionHandler = useCallback(
        ({
                changedInputType,
                changedCoinAvailablePairs,
                changedCoinCurrencyType,
                affectedCoinCurrencyType,
                affectedCoinDto,
            }) =>
            async (changedCoinDto: any) => {
                setCoinInputsLoading(true)

                try {
                    // 1. getNewAffectedList
                    const validChangedCoinPairList =
                        changedCoinAvailablePairs[changedCoinDto.code] ?? []

                    const {
                        // TODO have method only to calc new affected list
                        changedCoinList, // : updatedCoinListFrom,
                        affectedCoinList, // : updatedCoinListTo,
                    } = getCoinListOnCoinSelection(
                        currencies,
                        validChangedCoinPairList,
                        changedCoinCurrencyType,
                        affectedCoinCurrencyType,
                    )

                    // 2. getValidAffectedCoin
                    const validatedAffectedCoinDto = validateAffectedSelectedCoin(
                        affectedCoinDto.code,
                        affectedCoinList,
                    )

                    if (!validatedAffectedCoinDto) {
                        throw new Error(
                            'You are trying to select invalid pair. Our apologies for inconvenience.',
                        )
                    }

                    // 3. get new pair data:
                    const { data: apiPairDto, error } = await getPairData({
                        changedInputType,
                        changedCoinDto,
                        affectedCoinDto: validatedAffectedCoinDto,
                    })

                    // 4. handle new Pair Data:
                    if (apiPairDto) {
                        onGetPairSuccess(apiPairDto)
                        setAvailableCoinList({ changedInputType, affectedCoinList })
                    } else {
                        onGetPairError({
                            errorDto: error,
                            dataLayerEvent: 'fail_select_coin',
                        })
                    }
                } catch (ex) {
                    const pair = buyMode ? `${affectedCoinDto.code}${changedCoinDto.code}` : `${changedCoinDto.code}${affectedCoinDto.code}`
                    console.error(ex)
                    dispatchWarningAction(
                        new ShowWarningAction(
                            ex?.message &&
                                `${pair} pair is not available`
                        ),
                    )
                }

                setCoinInputsLoading(false)
            },
        [
            getPairData,
            currencies,
            setCoinInputsLoading,
            setAvailableCoinList,
            onGetPairSuccess,
            onGetPairError,
            dispatchWarningAction,
        ],
    )

    const handleFromCoinChange = useMemo(
        () =>
            getCoinSelectionHandler({
                changedInputType: buyMode ? ECoinInputType.FROM : ECoinInputType.TO,
                changedCoinAvailablePairs: buyMode ? fromPairs : toPairs,
                changedCoinCurrencyType: coinTypeFrom,
                affectedCoinCurrencyType: buyMode ? coinTypeTo : coinTypeFrom,
                affectedCoinDto: buyMode ? coinTo : coinFrom,
            }),
        [coinTo, coinTypeFrom, coinTypeTo, fromPairs, getCoinSelectionHandler, buyMode],
    )

    const handleToCoinChange = useMemo(
        () =>
            getCoinSelectionHandler({
                changedInputType: buyMode ? ECoinInputType.TO : ECoinInputType.FROM,
                changedCoinAvailablePairs: buyMode ? toPairs : fromPairs,
                changedCoinCurrencyType: buyMode ? coinTypeTo : coinTypeFrom,
                affectedCoinCurrencyType: buyMode ? coinTypeFrom : coinTypeTo,
                affectedCoinDto: buyMode ? coinFrom : coinTo,
            }),
        [coinTypeFrom, coinTypeTo, coinFrom, toPairs, getCoinSelectionHandler, buyMode],
    )

    return {
        handleFromCoinChange,
        handleToCoinChange,
    }
}

export { useCoinSelectionHandler }
